<template>
    <div class="manifest-ui import-runs">
        <HamletTable
            ref="table"
            :columns="columns"
            :fetch="fetch"
            :default-per-page="15"
            :page-options="[15, 30, 50]"
            @onRowClick="openRun"
        >
            <template #header-label>
                Import runs
            </template>

            <template #header-controls>
                <div class="import-runs__controls">
                    <b-form-select
                        v-model="statusFilter"
                        :options="statusOptions"
                        class="import-runs__status-filter"
                        aria-label="Filter import runs by status"
                        @change="refresh"
                    />
                    <b-button
                        variant="outline-secondary"
                        class="btn-icon"
                        title="Refresh runs"
                        @click="refresh"
                    >
                        <feather-icon icon="RefreshCwIcon" size="16" />
                    </b-button>
                    <b-button variant="primary" @click="startImport">
                        <feather-icon icon="PlusIcon" size="16" class="mr-50" />
                        New import
                    </b-button>
                </div>
            </template>

            <template #cell-manifest="data">
                <div v-if="data.item.manifest">
                    <div class="font-weight-bold" :title="data.item.manifest.description || ''">
                        {{ manifestName(data.item) }}
                    </div>
                    <small class="text-muted">
                        {{ manifestSubtitle(data.item) }}
                    </small>
                </div>
                <div v-else>
                    <div class="font-weight-bold">{{ manifestName(data.item) }}</div>
                    <small class="text-muted import-runs__technical-id" :title="data.item.controlRunId">
                        {{ manifestSubtitle(data.item) }}
                    </small>
                </div>
            </template>

            <template #cell-status="data">
                <b-badge :variant="statusVariant(data.item.status)">
                    {{ statusTitle(data.item.status) }}
                </b-badge>
            </template>

            <template #cell-progress="data">
                <div class="import-runs__progress">
                    <div class="d-flex justify-content-between mb-25 font-small-2">
                        <span>{{ progressLabel(data.item) }}</span>
                        <span>{{ progressPercent(data.item) }}%</span>
                    </div>
                    <b-progress :max="100" height=".35rem">
                        <b-progress-bar
                            :variant="progressVariant(data.item.status)"
                            :value="progressPercent(data.item)"
                        />
                    </b-progress>
                </div>
            </template>

            <template #cell-attempts="data">
                {{ Number(data.item.attempts || 0) }}
            </template>

            <template #cell-statistics="data">
                <span :title="statisticsTitle(data.item)">
                    {{ statisticsSummary(data.item) }}
                </span>
            </template>

            <template #cell-createdAt="data">
                {{ formatDate(data.item.createdAt) }}
            </template>

            <template #cell-completedAt="data">
                {{ formatDate(data.item.completedAt) }}
            </template>

            <template #no-records>
                {{ emptyMessage }}
            </template>
        </HamletTable>
    </div>
</template>

<script>
import {
    BBadge,
    BButton,
    BFormSelect,
    BProgress,
    BProgressBar,
} from 'bootstrap-vue'
import {ImportManifestApi} from '@core/api'
import HamletTable from '@core/components/common/HamletTable.vue'
import backendResponse from '@core/mixins/ui/backend-response'
import {getImportManifestTitle} from '../manifests/manifestContexts'

const ACTIVE_STATUSES = ['queued', 'importing']

export default {
    name: 'ImportRunsGrid',
    components: {
        BBadge,
        BButton,
        BFormSelect,
        BProgress,
        BProgressBar,
        HamletTable,
    },
    mixins: [backendResponse],
    data() {
        return {
            columns: [
                {key: 'manifest', label: 'Manifest'},
                {key: 'status', label: 'Status'},
                {key: 'progress', label: 'Progress'},
                {key: 'attempts', label: 'Attempts'},
                {key: 'statistics', label: 'Result'},
                {key: 'createdAt', label: 'Created at'},
                {key: 'completedAt', label: 'Completed at'},
            ],
            statusFilter: '',
            statusOptions: [
                {value: '', text: 'All statuses'},
                {value: 'queued,importing', text: 'Active'},
                {value: 'queued', text: 'Queued'},
                {value: 'importing', text: 'Importing'},
                {value: 'completed', text: 'Completed'},
                {value: 'failed', text: 'Failed'},
            ],
        }
    },
    computed: {
        emptyMessage() {
            return this.statusFilter
                ? 'No import runs match the selected status.'
                : 'No imports have been started yet.'
        },
    },
    methods: {
        fetch(config, callback) {
            const filters = {limit: 100}
            if (this.statusFilter) {
                filters.status = this.statusFilter
            }

            ImportManifestApi.listImportJobs(filters)
                .then(response => {
                    const jobs = Array.isArray(response.data) ? response.data : []
                    const start = (config.page - 1) * config.perPage
                    callback(jobs.length, jobs.slice(start, start + config.perPage))
                })
                .catch(error => {
                    callback(0, [])
                    this.handleBackendError(error)
                })
        },
        refresh() {
            if (this.$refs.table) {
                this.$refs.table.currentPage = 1
                this.$refs.table.makeRequest()
            }
        },
        openRun(job) {
            this.$router.push({
                name: 'administration-import-run',
                params: {id: job.id},
            })
        },
        startImport() {
            this.$router.push({name: 'administration-import'})
        },
        shortId(id) {
            return id ? `#${id.slice(0, 8)}` : '—'
        },
        manifestTypeTitle(manifestType) {
            return getImportManifestTitle(manifestType)
        },
        manifestName(job) {
            return job.manifest ? job.manifest.name : 'Manifest unavailable'
        },
        manifestSubtitle(job) {
            if (!job.manifest) {
                return `Control run ${job.controlRunId || '—'}`
            }
            return `${this.manifestTypeTitle(job.manifest.manifestType)} · Revision ${job.manifest.revision} · ${this.shortId(job.id)}`
        },
        statusTitle(status) {
            return {
                queued: 'Queued',
                importing: 'Importing',
                completed: 'Completed',
                failed: 'Failed',
            }[status] || status || 'Unknown'
        },
        statusVariant(status) {
            return {
                queued: 'light-secondary',
                importing: 'light-primary',
                completed: 'light-success',
                failed: 'light-danger',
            }[status] || 'light-secondary'
        },
        progressWeight(job) {
            return job && job.liveProgress && job.liveProgress.weight
                ? job.liveProgress.weight
                : {}
        },
        totalWeight(job) {
            const weight = this.progressWeight(job)
            return Number(weight.total || job.totalProgressWeight || 0)
        },
        processedWeight(job) {
            const total = this.totalWeight(job)
            if (job.status === 'completed') {
                return total
            }
            return Number(this.progressWeight(job).processed || 0)
        },
        progressPercent(job) {
            const total = this.totalWeight(job)
            if (!total) {
                return job.status === 'completed' ? 100 : 0
            }
            return Math.min(100, Math.round((this.processedWeight(job) / total) * 100))
        },
        progressLabel(job) {
            if (!ACTIVE_STATUSES.includes(job.status) && !job.liveProgress) {
                return job.status === 'completed' ? 'Finished' : 'Stopped'
            }
            return `${this.processedWeight(job)} / ${this.totalWeight(job)}`
        },
        progressVariant(status) {
            return status === 'completed' ? 'success' : status === 'failed' ? 'danger' : 'primary'
        },
        jobStatistics(job) {
            if (ACTIVE_STATUSES.includes(job.status) && job.liveProgress) {
                return job.liveProgress.statistics || {}
            }
            return job.statistics || {}
        },
        statisticsEntries(job) {
            return Object.entries(this.jobStatistics(job))
        },
        statisticsSummary(job) {
            const entries = this.statisticsEntries(job)
            if (!entries.length) {
                return '—'
            }
            const summary = entries.slice(0, 2)
                .map(([key, value]) => `${this.humanizeKey(key)}: ${value}`)
                .join(' · ')
            return entries.length > 2 ? `${summary} · +${entries.length - 2}` : summary
        },
        statisticsTitle(job) {
            return this.statisticsEntries(job)
                .map(([key, value]) => `${this.humanizeKey(key)}: ${value}`)
                .join(' · ')
        },
        humanizeKey(key) {
            const label = key
                .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
                .replace(/[_-]+/g, ' ')
            return label.charAt(0).toUpperCase() + label.slice(1)
        },
        formatDate(value) {
            return value ? this.$moment(value).format('YYYY-MM-DD HH:mm') : '—'
        },
    },
}
</script>

<style lang="scss">
@import '../manifests/theme';
</style>

<style lang="scss" scoped>
.import-runs__controls {
    display: flex;
    align-items: center;
    gap: .75rem;
}

.import-runs__status-filter {
    width: 10rem;
}

.import-runs__technical-id {
    display: block;
    overflow: hidden;
    max-width: 12rem;
    text-overflow: ellipsis;
}

.import-runs__progress {
    min-width: 10rem;
}

::v-deep .hamlet-table tbody tr {
    cursor: pointer;
}

::v-deep .hamlet-table td:first-child {
    min-width: 13rem;
}

::v-deep .hamlet-table td:nth-child(5) {
    max-width: 22rem;
    overflow: hidden;
    text-overflow: ellipsis;
}

@media (max-width: 767.98px) {
    .import-runs__controls {
        align-items: stretch;
        flex-direction: column;
    }

    .import-runs__status-filter {
        width: 100%;
    }
}
</style>
